import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generateQRCode } from '../actions/userActions';
import { toast } from 'react-toastify';
import './QRCodeModal.css';

const getSecondsLeft = (value) => {
  if (!value) return null;

  const expiryTime = new Date(value).getTime();
  if (Number.isNaN(expiryTime)) return null;

  return Math.max(0, Math.floor((expiryTime - Date.now()) / 1000));
};

const formatCountdown = (value) => {
  if (value == null) return 'Expiry unavailable';
  if (value <= 0) return 'QR expired';

  const hours = Math.floor(value / 3600);
  const minutes = Math.floor((value % 3600) / 60);
  const seconds = value % 60;

  if (hours > 0) {
    return `Expires in ${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m`;
  }

  if (minutes > 0) {
    return `Expires in ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;
  }

  return `Expires in ${String(seconds).padStart(2, '0')}s`;
};

const formatLocalTime = (value) => {
  if (!value) return 'Time unavailable';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Time unavailable';

  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatLocalDate = (value) => {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' });
};

const QRCodeModal = ({ isOpen, onClose }) => {
  const [qrCode, setQrCode] = useState('');
  const [securityCheckIn, setSecurityCheckIn] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [generatedDate, setGeneratedDate] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [qrType, setQrType] = useState('CHECK_IN');
  const [verificationState, setVerificationState] = useState(null);
  const dispatch = useDispatch();

  const userLogin = useSelector((state) => state.userLogin || {});
  const { userInfo } = userLogin;

  const resolvedVerification = verificationState || userInfo?.verification || {};
  const aadhaarVerified = resolvedVerification?.aadhaar?.verified === true;
  const panVerified = resolvedVerification?.pan?.verified === true;
  const bankVerified = resolvedVerification?.bank?.verified === true;
  const manualKYCApproved =
    resolvedVerification?.manualKYC?.status === 'approved'
    || resolvedVerification?.manualKYCStatus === 'approved';
  const isFullyVerified = resolvedVerification?.isFullyVerified === true
    || ((aadhaarVerified || panVerified) && manualKYCApproved);

  const fetchVerification = useCallback(async () => {
    if (!userInfo?.id) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/users/get-user-verification/${userInfo.id}`,
      );

      if (!response.ok) {
        throw new Error('Unable to refresh verification status');
      }

      const data = await response.json();

      if (data?.success) {
        setVerificationState({
          ...(data.verification || {}),
          manualKYC: data.manualKYC || null,
          manualKYCStatus: data.manualKYC?.status || null,
        });
      }
    } catch (error) {
      // Fall back to login-time verification if refresh fails.
      setVerificationState(null);
    }
  }, [userInfo?.id]);

  const fetchQRCode = useCallback(async ({ refresh = false } = {}) => {
    try {
      if (refresh) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }

      const data = await dispatch(generateQRCode());

      setQrCode(data?.qrCode || '');
      setSecurityCheckIn(data?.securityCheckIn || null);
      setExpiresAt(data?.expiresAt || null);
      setGeneratedDate(data?.generatedDate || '');
      setSecondsLeft(getSecondsLeft(data?.expiresAt));
      setQrType(data?.qrType || 'CHECK_IN');
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Unable to generate QR code right now.');
      setQrCode('');
      setSecurityCheckIn(null);
      setExpiresAt(null);
      setGeneratedDate('');
      setSecondsLeft(null);
      setQrType('CHECK_IN');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [dispatch]);

  const handleDownload = () => {
    if (!qrCode) return;

    const dateFragment = generatedDate || new Date().toISOString().split('T')[0];
    const link = document.createElement('a');
    link.href = qrCode;
    const prefix = qrType === 'CHECK_OUT' ? 'security-checkout-qr' : 'security-checkin-qr';
    link.download = `${prefix}-${dateFragment}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    if (isOpen) {
      fetchVerification();
    }
  }, [fetchVerification, isOpen]);

  useEffect(() => {
    if (isOpen && isFullyVerified) {
      fetchQRCode();
    }
  }, [fetchQRCode, isOpen, isFullyVerified]);

  useEffect(() => {
    if (!isOpen) {
      setQrCode('');
      setSecurityCheckIn(null);
      setExpiresAt(null);
      setGeneratedDate('');
      setSecondsLeft(null);
      setLoading(false);
      setIsRefreshing(false);
      setQrType('CHECK_IN');
      setVerificationState(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !expiresAt) return undefined;

    const tick = () => {
      setSecondsLeft(getSecondsLeft(expiresAt));
    };

    tick();
    const timerId = setInterval(tick, 1000);
    return () => clearInterval(timerId);
  }, [expiresAt, isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const checkedInToday = Boolean(securityCheckIn?.checkedInToday);
  const checkedOutToday = Boolean(securityCheckIn?.checkedOutToday);
  const currentStatus = securityCheckIn?.currentStatus || 'NOT_CHECKED_IN';
  const countdownLabel = formatCountdown(secondsLeft);
  const isExpired = secondsLeft !== null && secondsLeft <= 0;
  const generatedLabel = formatLocalDate(generatedDate);
  const isCheckOut = qrType === 'CHECK_OUT';

  return (
    <div
      className="qr-modal-overlay"
      data-testid="qr-modal-overlay"
      onClick={onClose}
    >
      <div
        aria-labelledby="qr-modal-title"
        aria-modal="true"
        className="qr-modal-card"
        role="dialog"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="qr-modal-header">
          <div>
            <h2 className="qr-modal-title" id="qr-modal-title">
              {isCheckOut ? 'Security Check-Out QR' : 'Security Check-In QR'}
            </h2>
            <p className="qr-modal-subtitle">
              {isCheckOut ? 'Show this code when leaving' : 'Show this code at entry'}
            </p>
          </div>
          <button
            aria-label="Close QR code popup"
            className="qr-modal-close"
            onClick={onClose}
            type="button"
          >
            <svg aria-hidden="true" fill="none" height="20" stroke="currentColor" viewBox="0 0 24 24" width="20">
              <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
            </svg>
          </button>
        </div>

        <div className="qr-modal-body">
          {!isFullyVerified ? (
            <div className="qr-verification-card">
              <h3 className="qr-verification-title">Verification Required</h3>
              <p className="qr-verification-copy">
                Your QR code becomes available once at least one KYC document is approved. Bank verification is optional.
              </p>

              <div className="qr-verification-list">
                <div className={`qr-verification-row ${aadhaarVerified ? 'is-verified' : 'is-pending'}`}>
                  <span>Aadhaar</span>
                  <span>{aadhaarVerified ? 'Verified' : manualKYCApproved ? 'Not submitted' : 'Pending'}</span>
                </div>
                <div className={`qr-verification-row ${panVerified ? 'is-verified' : 'is-pending'}`}>
                  <span>PAN</span>
                  <span>{panVerified ? 'Verified' : manualKYCApproved ? 'Not submitted' : 'Pending'}</span>
                </div>
                <div className={`qr-verification-row ${bankVerified ? 'is-verified' : 'is-pending'}`}>
                  <span>Bank</span>
                  <span>{bankVerified ? 'Verified' : 'Optional'}</span>
                </div>
              </div>
            </div>
          ) : loading ? (
            <div className="qr-loading-wrap" role="status">
              <div aria-label="Loading QR code" className="qr-loading-spinner" />
            </div>
          ) : (
            <>
              <div className={`qr-status-card ${
                currentStatus === 'CHECKED_OUT' ? 'is-checkout'
                  : checkedInToday ? 'is-success'
                  : 'is-pending'
              }`}>
                <span className="qr-status-badge">
                  {currentStatus === 'CHECKED_OUT'
                    ? 'Checked out today'
                    : checkedInToday
                      ? 'Checked in today'
                      : 'Not checked in today'}
                </span>
                <p className="qr-status-line">
                  {currentStatus === 'CHECKED_OUT'
                    ? 'You have been checked out. Scan below to check in again.'
                    : checkedInToday
                      ? (securityCheckIn?.clubName ? `Club: ${securityCheckIn.clubName}` : 'Club information unavailable')
                      : 'Scan this code at your club entrance to complete check-in.'}
                </p>
                {checkedInToday && (
                  <p className="qr-status-meta">
                    {securityCheckIn?.locationString || 'Location unavailable'} | {formatLocalTime(securityCheckIn?.checkedInAt)}
                    {checkedOutToday && securityCheckIn?.checkedOutAt && (
                      <> | Out: {formatLocalTime(securityCheckIn.checkedOutAt)}</>
                    )}
                  </p>
                )}
              </div>

              {qrCode ? (
                <div className="qr-image-panel">
                  <img alt={isCheckOut ? 'Security check-out QR code' : 'Security check-in QR code'} className="qr-image" src={qrCode} />
                </div>
              ) : (
                <div className="qr-empty-state">
                  QR code is not available right now. Please refresh and try again.
                </div>
              )}

              <div className="qr-meta-row">
                <span className={`qr-expiry-pill ${isExpired ? 'is-expired' : ''}`}>
                  {countdownLabel}
                </span>
                {generatedLabel && (
                  <span className="qr-generated-label">Generated {generatedLabel}</span>
                )}
              </div>

              <div className="qr-actions">
                <button
                  className="qr-button qr-button-primary"
                  disabled={loading || isRefreshing}
                  onClick={() => fetchQRCode({ refresh: true })}
                  type="button"
                >
                  {isRefreshing ? 'Refreshing...' : 'Refresh QR'}
                </button>
                <button
                  className="qr-button qr-button-secondary"
                  disabled={!qrCode || loading || isRefreshing}
                  onClick={handleDownload}
                  type="button"
                >
                  Download QR
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default QRCodeModal;
